import React, { useState, useEffect } from 'react';
import { Search, Plus, MoreVertical, Edit, Trash2, UserCheck, UserX, Eye, EyeOff, RefreshCw, User } from 'lucide-react';
import { usersAPI } from '../../services/api';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state - using username instead of email
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    role: 'Starter'
  });
  
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name || !formData.username || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await usersAPI.create(formData);
      setSuccess('User created successfully');
      setShowModal(false);
      setFormData({ name: '', username: '', password: '', role: 'Starter' });
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setLoading(true);
    try {
      const updateData = {};
      if (formData.name) updateData.name = formData.name;
      if (formData.username) updateData.username = formData.username;
      if (formData.password) updateData.password = formData.password;
      if (formData.role) updateData.role = formData.role;
      
      await usersAPI.update(editingUser.id, updateData);
      setSuccess('User updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      setFormData({ name: '', username: '', password: '', role: 'Starter' });
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    setLoading(true);
    try {
      await usersAPI.update(userId, { status: newStatus });
      setSuccess(`User ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update user status');
    } finally {
      setLoading(false);
      setActionMenu(null);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    setLoading(true);
    try {
      await usersAPI.delete(userId);
      setSuccess('User deleted successfully');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    } finally {
      setLoading(false);
      setActionMenu(null);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      username: user.username || '',
      password: '',
      role: user.role
    });
    setShowEditModal(true);
    setActionMenu(null);
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">User Management</h1>
          <p className="text-gray-400">Create and manage users with login credentials</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="flex items-center gap-2 border border-gray-700 text-gray-300 hover:bg-gray-800 px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => {
              setFormData({ name: '', username: '', password: '', role: 'Starter' });
              setShowModal(true);
            }}
            className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add User
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-400 hover:text-red-300">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
          {success}
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search users by name or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-12 pr-4 py-2.5 text-white placeholder-gray-500 outline-none focus:border-teal-400"
            />
          </div>
          <select className="bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400">
            <option>All Roles</option>
            <option>Enterprise</option>
            <option>Pro</option>
            <option>Starter</option>
          </select>
          <select className="bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400">
            <option>All Status</option>
            <option>Active</option>
            <option>Pending</option>
            <option>Suspended</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1a1a1f]">
              <tr>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">USER</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">ROLE</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">AGENTS</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">CREATED</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">STATUS</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    {loading ? 'Loading users...' : 'No users found. Click "Add User" to create one.'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="border-t border-gray-800 hover:bg-[#1a1a1f]/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-black font-bold">
                          {user.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-gray-500 text-sm flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {user.username || 'No username'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        user.role === 'Enterprise' ? 'bg-purple-400/10 text-purple-400' :
                        user.role === 'Pro' ? 'bg-blue-400/10 text-blue-400' :
                        'bg-gray-400/10 text-gray-400'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-300">{user.agents || 0}</td>
                    <td className="px-6 py-4 text-gray-300">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        user.status === 'active' ? 'bg-green-400/10 text-green-400' :
                        user.status === 'pending' ? 'bg-yellow-400/10 text-yellow-400' :
                        'bg-red-400/10 text-red-400'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative">
                        <button
                          onClick={() => setActionMenu(actionMenu === user.id ? null : user.id)}
                          className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-400" />
                        </button>
                        {actionMenu === user.id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-[#1a1a1f] border border-gray-700 rounded-lg shadow-xl z-10">
                            <button
                              onClick={() => openEditModal(user)}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-gray-300 hover:bg-gray-800 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                              Edit User
                            </button>
                            {user.status === 'active' ? (
                              <button
                                onClick={() => handleStatusChange(user.id, 'suspended')}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-yellow-400 hover:bg-gray-800 transition-colors"
                              >
                                <UserX className="w-4 h-4" />
                                Suspend User
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStatusChange(user.id, 'active')}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-green-400 hover:bg-gray-800 transition-colors"
                              >
                                <UserCheck className="w-4 h-4" />
                                Activate User
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-red-400 hover:bg-gray-800 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete User
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-6">Create New User</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter username (for login)"
                  required
                />
                <p className="text-gray-500 text-xs mt-1">Users will login with this username</p>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 pr-12 text-white outline-none focus:border-teal-400"
                    placeholder="Enter password (min 6 characters)"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                >
                  <option value="Starter">Starter</option>
                  <option value="Pro">Pro</option>
                  <option value="Enterprise">Enterprise</option>
                </select>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-700 text-gray-300 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black py-2.5 rounded-lg font-medium transition-colors"
                >
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-6">Edit User</h3>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter username"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">New Password (leave blank to keep current)</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 pr-12 text-white outline-none focus:border-teal-400"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                >
                  <option value="Starter">Starter</option>
                  <option value="Pro">Pro</option>
                  <option value="Enterprise">Enterprise</option>
                </select>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  className="flex-1 border border-gray-700 text-gray-300 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black py-2.5 rounded-lg font-medium transition-colors"
                >
                  {loading ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
